import type { GraphSearchItem, GraphSite } from "../../email/services/graph";
import {
  formatFileSize,
  formatRelativeDate,
  getFileExtension,
  getFileIconClass,
  getFileIconKind,
  getFileIconLabel,
  getTagStyle,
} from "../../files/components/fileRowMeta";
import styles from "../../files/components/FolderBrowser.module.css";
import kb from "./KnowledgeModule.module.css";

interface KnowledgeSearchResultsProps {
  readonly results: GraphSearchItem[];
  /** Site collections whose name matched the query — shown above the file hits. */
  readonly sites?: GraphSite[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly onResultClick: (item: GraphSearchItem) => void;
  readonly onSiteClick?: (site: GraphSite) => void;
}

function buildMeta(item: GraphSearchItem): string {
  return [item.sourceLabel, formatFileSize(item.size), formatRelativeDate(item.lastModifiedDateTime)]
    .filter(Boolean)
    .join(" · ");
}

function siteLabel(site: GraphSite): string {
  return site.displayName || site.name || "Untitled site";
}

/** Up-to-two-letter initials for a site's coloured square (mirrors the site list). */
function siteInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "SP";
  return trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

/**
 * Flat, cross-tenant search results for the Knowledge Base. Reuses FolderBrowser's
 * row styling (via its CSS module) and the shared file-row helpers so results
 * look identical to the browse rows. Matching site collections (by name) are
 * listed first as drill-in rows, then the file / folder hits. Each row's meta
 * line shows the source library/folder.
 */
export function KnowledgeSearchResults({
  results,
  sites = [],
  loading,
  error,
  onResultClick,
  onSiteClick,
}: Readonly<KnowledgeSearchResultsProps>) {
  const nothingFound = !loading && !error && results.length === 0 && sites.length === 0;
  return (
    <div className={styles.list}>
      {error && <div className={styles.error}>{error}</div>}
      {loading && <div className={styles.empty}>Searching…</div>}
      {nothingFound && (
        <div className={styles.empty}>No documents match your search.</div>
      )}

      {!loading &&
        sites.map((site) => {
          const name = siteLabel(site);
          return (
            <button
              key={`site-${site.id}`}
              type="button"
              className={kb.site}
              onClick={() => onSiteClick?.(site)}
            >
              <div className={kb.siteIcon}>{siteInitials(name)}</div>
              <div className={kb.siteBody}>
                <div className={kb.siteName}>{name}</div>
                <div className={kb.siteDesc}>{site.description || "Site collection"}</div>
              </div>
              <span className={kb.chev} aria-hidden="true">›</span>
            </button>
          );
        })}

      {!loading &&
        results.map((item) => {
          const ext = getFileExtension(item.name);
          const kind = getFileIconKind(ext);
          return (
            <button
              key={item.id}
              type="button"
              className={styles.row}
              onClick={() => onResultClick(item)}
            >
              {item.isFolder ? (
                <div className={`${styles.icon} ${styles.iconFolder}`}>📁</div>
              ) : (
                <div className={`${styles.icon} ${getFileIconClass(kind)}`}>{getFileIconLabel(ext)}</div>
              )}
              <div className={styles.body}>
                <div className={styles.name}>{item.name || "Folder"}</div>
                <div className={styles.meta}>{buildMeta(item)}</div>
              </div>
              {item.tags && item.tags.length > 0 && (
                <div className={styles.rowTags}>
                  {item.tags.map((t) => (
                    <span key={t} className={styles.tagChip} style={getTagStyle(t)}>{t}</span>
                  ))}
                </div>
              )}
              {item.isFolder && (
                <span className={styles.chev} aria-hidden="true">›</span>
              )}
            </button>
          );
        })}
    </div>
  );
}
